<script lang="ts" setup>
import type AssetBooking from '@/classes/assetBooking'
import Charger from '@/classes/charger_classes/charger'
import type EVSE from '@/classes/charger_classes/evse'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import DeviceCard from '@/components/charging-stations/DeviceCard.vue'
import { GettersTypes, type State } from '@/store/store_types'
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'

// hooks

const store = useStore<State>()
const { mobile } = useDisplay()

// data

const booking = computed<AssetBooking | undefined>(
  () => store.getters[GettersTypes.getDisplayedBooking],
)

const isMultiDay = computed(() => {
  if (booking.value) {
    const startDate = booking.value.start_date
    const endDate = booking.value.end_date
    const sameDay = startDate.toDateString() === endDate.toDateString()
    return !sameDay
  }
  return false
})

const evseData = computed<EVSE | undefined>(() => {
  if (
    booking.value &&
    booking.value.asset_type === 'charger' &&
    booking.value.asset_reference instanceof Charger
  ) {
    const evse = (booking.value.asset_reference as Charger).evses.find(
      (evse) => evse.uid === booking.value!.asset_id,
    )
    return evse
  }
  return undefined
})
</script>
<template>
  <v-card flat class="bg-pwt_snow py-10">
    <!-- vehicle booking -->
    <div v-if="booking && booking.asset_type === 'vehicle'" class="pa-4">
      <!-- vehicle card -->
      <v-card rounded="lg" class="d-flex align-center pa-4" elevation="3" color="pwt_snow">
        <!-- vehicle image column -->
        <v-col cols="12" md="2">
          <v-img
            contain
            :src="booking.displayImgSrc"
            aspect-ratio="1"
            :width="mobile ? '40%' : '100%'"
            :height="mobile ? 'auto' : '100%'"
            style="margin: auto"
          ></v-img>
        </v-col>
        <!-- vehicle details column -->
        <v-col cols="12" md="10">
          <v-card-title class="pb-0">
            {{ (booking.asset_reference as Vehicle | undefined)?.licensePlate }}
          </v-card-title>
          <v-card-subtitle class="pt-0">
            {{ (booking.asset_reference as Vehicle | undefined)?.name }}
          </v-card-subtitle>
          <v-row no-gutters class="px-4 mt-2">
            <v-col cols="4" class="d-flex align-center">
              <v-icon color="primary" size="small" class="mr-1">
                {{ (booking.asset_reference as Vehicle | undefined)?.fuelTypeIcon }}
              </v-icon>
              {{ (booking.asset_reference as Vehicle | undefined)?.fuelTypeDisplayText }}
            </v-col>
            <v-col cols="4" class="d-flex align-center">
              <v-icon color="primary" size="small" class="mr-1">
                {{ (booking.asset_reference as Vehicle | undefined)?.fuelCapacityIcon }}
              </v-icon>
              {{ (booking.asset_reference as Vehicle | undefined)?.fuelCapacityDisplayText }}
            </v-col>
            <v-col
              v-if="(booking.asset_reference as Vehicle | undefined)?.bootCapacityText"
              cols="4"
              class="d-flex align-center"
            >
              <v-icon color="primary" size="small" class="mr-1">
                mdi-package-variant-closed
              </v-icon>
              {{ (booking.asset_reference as Vehicle | undefined)?.bootCapacityText }}
            </v-col>
          </v-row>
        </v-col>
      </v-card>
      <!-- booking details -->
      <v-card class="pa-4 mt-5" flat color="pwt_snow">
        <!-- booking date -->
        <v-card-title>
          {{
            isMultiDay
              ? booking.formattedStartDate + ' - ' + booking.formattedEndDate
              : booking.formattedStartDate
          }}
        </v-card-title>
        <v-row no-gutters>
          <!-- booking  start time column -->
          <v-col cols="6" class="pr-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking started at </span>
                <span class="text-body-1"> {{ booking.formattedStartTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
          <!-- booking end time column -->
          <v-col cols="6" class="pl-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking ended at </span>
                <span class="text-body-1"> {{ booking.formattedEndTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <!-- booking extra details section  -->
      </v-card>
    </div>
    <!-- charger booking -->
    <div v-if="booking && booking.asset_type === 'charger'">
      <v-card rounded="lg" class="pa-4 ma-2 mb-5" elevation="3" color="pwt_snow">
        <v-card-title class="py-0">
          {{ (booking.asset_reference as Charger).name }}
        </v-card-title>
        <v-card-subtitle opacity="1">
          {{ (booking.asset_reference as Charger).addressString }}</v-card-subtitle
        >
        <v-card-text>
          <!-- basic details section -->
          <ul class="text-body-1" style="list-style: none; padding-left: unset">
            <li>data provided by {{ (booking.asset_reference as Charger).dataSrcDisplayStr }}</li>
            <li>{{ (booking.asset_reference as Charger).evses.length }} devices</li>
            <li>
              {{ (booking.asset_reference as Charger).simpleDisplayRating }}
            </li>
          </ul>
        </v-card-text>
      </v-card>
      <v-card-title>{{ booking.formattedStartDate }}</v-card-title>
      <v-card-text>
        <v-row no-gutters>
          <!-- booking  start time column -->
          <v-col cols="6" class="pr-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking started at </span>
                <span class="text-body-1"> {{ booking.formattedStartTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
          <!-- booking end time column -->
          <v-col cols="6" class="pl-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking ended at </span>
                <span class="text-body-1"> {{ booking.formattedEndTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-title> Details </v-card-title>
      <v-card-text>
        <DeviceCard
          v-if="evseData"
          flat
          :device="evseData"
          :bookable-device-data="
            (booking.asset_reference as Charger).bookableEVSEDisplayData.find(
              (bookableData) => bookableData.evse_id === evseData!.uid,
            )
          "
        />
      </v-card-text>
    </div>
    <!-- unknowing booking type -->
    <div v-else-if="booking"></div>
    <!-- empty state -->
    <div v-else></div>
  </v-card>
</template>
